import { z } from 'zod'
import { type ApiError as ApiErrorType, ApiErrorSchema } from '../types'

// Runtime configuration injected by entrypoint.sh at container startup
interface RuntimeConfig {
  API_BASE_URL?: string
}

// Runtime configuration for production (injected by entrypoint.sh)
// Falls back to Vite env var for development
function getApiBaseUrl(): string {
  if (import.meta.env.DEV) {
    return ''
  }

  const runtimeConfig = (window as Window & { __RUNTIME_CONFIG__?: RuntimeConfig }).__RUNTIME_CONFIG__
  const envUrl = import.meta.env.VITE_API_BASE_URL ?? ''

  const isSentinel = (v?: string) => !v || v === '__API_BASE_URL__'

  const resolve = (runtime?: string, env?: string): string => {
    const candidates = [runtime, env]
    for (const raw of candidates) {
      if (isSentinel(raw)) continue
      const v = (raw ?? '').trim().replace(/^['"]|['"]$/g, '')
      if (!v) continue

      // Absolute URL
      try {
        const u = new URL(v)
        return (u.origin + (u.pathname === '/' ? '' : u.pathname)).replace(/\/+$/g, '')
      } catch {
        // not absolute
      }

      // Protocol-relative
      if (v.startsWith('//')) {
        return `${window.location.protocol}${v}`.replace(/\/+$/g, '')
      }

      // Relative path
      if (v.startsWith('/')) {
        return (window.location.origin + v).replace(/\/+$/g, '')
      }

      // Host-only
      try {
        return `${window.location.protocol}//${v}`.replace(/\/+$/g, '')
      } catch {
        continue
      }
    }

    return window.location.origin
  }

  const resolved = resolve(runtimeConfig?.API_BASE_URL, envUrl)
  console.info('[API] Resolved API base URL:', resolved)
  return resolved
}

export const API_BASE_URL = getApiBaseUrl()

// CSRF cookie name (must match backend's JWT_ACCESS_CSRF_COOKIE_NAME)
const CSRF_COOKIE_NAME = 'csrf_access_token'

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get CSRF token from cookies.
 * The backend sets this cookie (non-HttpOnly) alongside the access token.
 */
function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value)
    }
  }
  return null
}

// =============================================================================
// Utility Functions for Case Conversion
// =============================================================================

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

function transformKeys<T = unknown>(obj: T, transformer: (key: string) => string): T {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(item => transformKeys(item, transformer)) as T
  }

  const transformed: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    // Transform key and recursively transform nested values
    transformed[transformer(key)] = transformKeys(value, transformer)
  }
  return transformed as T
}

// =============================================================================
// Custom Error Class
// =============================================================================

export class ApiError extends Error {
  public status: number
  public error: string
  public details?: Record<string, unknown>

  constructor(message: string, status: number, error: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.error = error
    if (details !== undefined) {
      this.details = details
    }
  }
}

// =============================================================================
// API Service
// =============================================================================

/**
 * Centralized API service for all HTTP requests.
 *
 * Features:
 * - Automatic camelCase/snake_case conversion
 * - Automatic 401 handling with token refresh
 * - Request deduplication for token refresh
 * - Zod schema validation for responses
 *
 * Authentication:
 * - All requests include `credentials: 'include'` to send HttpOnly cookies
 * - On 401, attempts to refresh token once, then retries the original request
 * - If refresh fails, triggers auth failure handler (logout + redirect)
 */
export class ApiService {
  private baseUrl: string
  // Endpoints that should skip token refresh on 401 (they may return 401 for other reasons)
  private skipRefreshEndpoints = [
    '/api/v1/auth/login',
    '/api/v1/auth/register',
    '/api/v1/auth/refresh',
    '/api/v1/auth/logout',
    '/api/v1/two-factor/setup',
    '/api/v1/two-factor/enable',
    '/api/v1/two-factor/verify',
  ]

  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = (baseUrl ?? '').toString()
  }

  // ===========================================================================
  // Public API Methods
  // ===========================================================================

  async get<T>(endpoint: string, schema?: z.ZodSchema<T>): Promise<T> {
    return this.request(endpoint, { method: 'GET' }, schema)
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit,
    schema?: z.ZodSchema<T>
  ): Promise<T> {
    const requestConfig: RequestInit = {
      method: 'POST',
      ...options,
    }

    // Only stringify data if it's not FormData
    if (data && !requestConfig.body) {
      if (data instanceof FormData) {
        requestConfig.body = data
      } else {
        requestConfig.body = JSON.stringify(data)
      }
    }

    return this.request(endpoint, requestConfig, schema)
  }

  async put<T>(endpoint: string, data?: unknown, schema?: z.ZodSchema<T>): Promise<T> {
    const requestConfig: RequestInit = {
      method: 'PUT',
    }

    if (data) {
      requestConfig.body = JSON.stringify(data)
    }

    return this.request(endpoint, requestConfig, schema)
  }

  async delete<T>(endpoint: string, data?: unknown, schema?: z.ZodSchema<T>): Promise<T> {
    const requestConfig: RequestInit = {
      method: 'DELETE',
    }

    if (data) {
      requestConfig.body = JSON.stringify(data)
    }

    return this.request(endpoint, requestConfig, schema)
  }

  async patch<T>(endpoint: string, data?: unknown, schema?: z.ZodSchema<T>): Promise<T> {
    const requestConfig: RequestInit = {
      method: 'PATCH',
    }

    if (data) {
      requestConfig.body = JSON.stringify(data)
    }

    return this.request(endpoint, requestConfig, schema)
  }

  // ===========================================================================
  // Core Request Method
  // ===========================================================================

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    schema?: z.ZodSchema<T>,
    isRetry = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const method = options.method?.toUpperCase() || 'GET'

    // Don't set Content-Type for FormData - let the browser set it automatically
    const isFormData = options.body instanceof FormData
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers as Record<string, string>),
    }

    // Add CSRF token for state-changing methods (POST, PUT, PATCH, DELETE)
    // The backend requires this when JWT_COOKIE_CSRF_PROTECT is enabled
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrfToken = getCsrfToken()
      if (csrfToken) {
        headers['X-CSRF-TOKEN'] = csrfToken
      }
    }

    const config: RequestInit = {
      headers,
      credentials: 'include', // Send HttpOnly cookies with requests
      ...options,
    }

    // Convert request body from camelCase to snake_case
    if (config.body && typeof config.body === 'string') {
      try {
        const parsedBody = JSON.parse(config.body)
        const transformedBody = transformKeys(parsedBody, camelToSnake)
        config.body = JSON.stringify(transformedBody)
      } catch {
        // If parsing fails, leave the body as-is
      }
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        return this.handleErrorResponse(response, endpoint, options, schema, isRetry)
      }

      return this.handleSuccessResponse(response, schema)
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      // Network or other errors
      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        0,
        'NETWORK_ERROR'
      )
    }
  }

  // ===========================================================================
  // Response Handlers
  // ===========================================================================

  private async handleSuccessResponse<T>(response: Response, schema?: z.ZodSchema<T>): Promise<T> {
    const data = await response.json()

    // Convert response data from snake_case to camelCase
    const transformedData = transformKeys(data, snakeToCamel)

    if (schema) {
      return schema.parse(transformedData)
    }

    return transformedData
  }

  private async handleErrorResponse<T>(
    response: Response,
    endpoint: string,
    options: RequestInit,
    schema?: z.ZodSchema<T>,
    isRetry = false
  ): Promise<T> {
    // Parse error response
    const errorData = await this.parseErrorResponse(response)

    // Handle 429 rate limit errors with specific message
    if (response.status === 429) {
      console.warn('[ApiService] Rate limit exceeded on:', endpoint)
      throw new ApiError(
        'auth.rateLimitExceeded',
        429,
        'RATE_LIMIT_EXCEEDED',
        { retryAfter: response.headers.get('Retry-After') }
      )
    }

    // Handle 401 with token refresh (only if this is not already a retry)
    if (response.status === 401 && !isRetry) {
      return this.handle401Error(endpoint, options, schema, errorData)
    }

    throw new ApiError(errorData.message, errorData.status, errorData.error, errorData.details)
  }

  private async parseErrorResponse(response: Response): Promise<ApiErrorType> {
    try {
      const errorJson = await response.json()
      const parsed = ApiErrorSchema.parse(errorJson)
      const message =
        typeof parsed.message === 'string' ? parsed.message : JSON.stringify(parsed.message)
      const result: ApiErrorType = {
        error: parsed.code.toString(),
        message,
        status: response.status,
      }
      if (parsed.details !== undefined) {
        result.details = parsed.details
      }
      return result
    } catch {
      return {
        error: 'UNKNOWN_ERROR',
        message: `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
      }
    }
  }

  // ===========================================================================
  // 401 Handling with Token Refresh
  // ===========================================================================

  private async handle401Error<T>(
    endpoint: string,
    options: RequestInit,
    schema?: z.ZodSchema<T>,
    errorData?: ApiErrorType
  ): Promise<T> {
    // Check if this endpoint should skip refresh attempts
    if (this.shouldSkipRefresh(endpoint)) {
      throw new ApiError(
        errorData?.message || 'Unauthorized',
        errorData?.status || 401,
        errorData?.error || 'UNAUTHORIZED',
        errorData?.details
      )
    }

    // Check if user appears to be authenticated (has stored user data)
    const { authService } = await import('./auth')
    if (!authService.isAuthenticated()) {
      // Not authenticated - don't try to refresh, just fail
      throw new ApiError(
        errorData?.message || 'Authentication required',
        errorData?.status || 401,
        errorData?.error || 'AUTHENTICATION_REQUIRED',
        errorData?.details
      )
    }

    // Attempt token refresh (with deduplication handled by authService)
    try {
      console.debug('[ApiService] 401 received, attempting token refresh for:', endpoint)
      await authService.refreshTokenWithDedup()
      console.debug('[ApiService] Token refresh succeeded, retrying original request:', endpoint)

      // Retry the original request (mark as retry to prevent infinite loop)
      return this.request(endpoint, options, schema, true)
    } catch (refreshError) {
      console.error('[ApiService] Token refresh failed for:', endpoint, refreshError)

      // Don't call handleAuthFailure here - authService.refreshTokenWithDedup already handles it
      // Just throw the error to let the caller know the request failed
      throw new ApiError(
        errorData?.message || 'Session expired',
        errorData?.status || 401,
        'SESSION_EXPIRED',
        errorData?.details
      )
    }
  }

  private shouldSkipRefresh(endpoint: string): boolean {
    return this.skipRefreshEndpoints.some(skip => endpoint.includes(skip))
  }
}

export const apiService = new ApiService()
